import { isValidElement } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { toast as sonnerToast } from 'sonner';
import type { ExternalToast } from 'sonner';

import type { PlatformStatus } from '../../alerts/platformStatus';
import FavoriteStatusIcon from '../../icons/FavoriteStatusIcon';
import { normalizeToastDescription } from './description';
import PlatformToastCard from './PlatformToastCard';
import { createPlatformToastResourceAction } from './resourceActions';
import type {
    PlatformToastAction,
    PlatformToastLane,
    PlatformToastOptions,
    PlatformToastUndo,
    StrictPlatformToastOptions,
} from './types';

export const PLATFORM_FEEDBACK_TOASTER_ID = 'platform-feedback';
export const PLATFORM_IN_APP_TOASTER_ID = 'platform-in-app';

type ToastTitle = (() => ReactNode) | ReactNode;
type ToastStatus = PlatformStatus | 'loading';
type ToastId = string | number;

const DEFAULT_DURATIONS: Record<PlatformToastLane, number> = {
    feedback: 5000,
    'in-app': 9000,
};

const DEFAULT_TITLES: Record<ToastStatus, string> = {
    default: 'Notification',
    error: 'Action Unsuccessful',
    info: 'Information',
    loading: 'Action in Progress',
    success: 'Action Completed',
    warning: 'Review Required',
};

const DEFAULT_DESCRIPTIONS: Record<ToastStatus, string> = {
    default: 'The requested update has been processed.',
    error: 'The requested action could not be completed. Please try again.',
    info: 'Additional information is available for this update.',
    loading: 'The requested action is currently in progress.',
    success: 'The requested action completed successfully.',
    warning: 'Review this update before continuing.',
};

const resolveNode = (node: (() => ReactNode) | ReactNode): ReactNode => (typeof node === 'function' ? node() : node);

const isLegacyAction = (
    action: ExternalToast['action'] | ExternalToast['cancel'],
): action is Exclude<ExternalToast['action'], ReactNode> =>
    Boolean(
        action && typeof action === 'object' && !isValidElement(action) && 'label' in action && 'onClick' in action,
    );

const isUndoLabel = (label: ReactNode): boolean => typeof label === 'string' && /\bundo\b/i.test(label);

const getFavoriteToastState = (message: ReactNode): boolean | undefined => {
    if (typeof message !== 'string') {
        return undefined;
    }

    const normalizedMessage = message.trim();

    if (/^added to favorites$/i.test(normalizedMessage)) {
        return true;
    }

    if (/^removed from favorites$/i.test(normalizedMessage)) {
        return false;
    }

    return undefined;
};

const wrapLegacyAction = (
    action: Exclude<ExternalToast['action'], ReactNode>,
    toastId: ToastId,
): PlatformToastAction => ({
    label: action.label,
    onClick: async (event) => {
        await action.onClick(event);
        sonnerToast.dismiss(toastId);
    },
});

const createToast = (
    status: ToastStatus,
    message: ToastTitle,
    options: PlatformToastOptions = {},
    forcedLane?: PlatformToastLane,
): ToastId => {
    const {
        action,
        actions = [],
        actionsAlwaysVisible = false,
        cancel,
        description,
        descriptionKind = 'auto',
        duration: requestedDuration,
        icon,
        lane: requestedLane,
        metadata,
        resource,
        style,
        timestamp = new Date(),
        undo: requestedUndo,
        ...sonnerOptions
    } = options;
    const lane = forcedLane ?? requestedLane ?? 'feedback';
    const duration = requestedDuration ?? requestedUndo?.duration ?? DEFAULT_DURATIONS[lane];
    const resolvedMessage = resolveNode(message);
    const favoriteState = getFavoriteToastState(resolvedMessage);
    const messageIsLegacyDescription = description === undefined;
    const resolvedTitle = messageIsLegacyDescription
        ? DEFAULT_TITLES[status]
        : resolvedMessage || DEFAULT_TITLES[status];
    const rawDescription =
        description === undefined
            ? messageIsLegacyDescription
                ? resolvedMessage || DEFAULT_DESCRIPTIONS[status]
                : DEFAULT_DESCRIPTIONS[status]
            : resolveNode(description);
    const resolvedDescription = normalizeToastDescription(
        rawDescription,
        messageIsLegacyDescription ? 'explanatory' : descriptionKind,
    );
    const toasterId = lane === 'in-app' ? PLATFORM_IN_APP_TOASTER_ID : PLATFORM_FEEDBACK_TOASTER_ID;

    return sonnerToast.custom(
        (toastId) => {
            const resourceAction = resource ? createPlatformToastResourceAction(resource) : undefined;
            const resolvedActions: Array<PlatformToastAction | ReactNode> = [
                ...(resourceAction ? [resourceAction] : []),
                ...actions,
            ];
            let undo: PlatformToastUndo | undefined = requestedUndo;

            if (isLegacyAction(action)) {
                if (!undo && isUndoLabel(action.label)) {
                    undo = {
                        duration,
                        onUndo: (event) => action.onClick(event as MouseEvent<HTMLButtonElement>),
                    };
                } else if (!isUndoLabel(action.label)) {
                    resolvedActions.push(wrapLegacyAction(action, toastId));
                }
            } else if (action) {
                resolvedActions.push(action);
            }

            if (isLegacyAction(cancel)) {
                resolvedActions.push(wrapLegacyAction(cancel, toastId));
            } else if (cancel) {
                resolvedActions.push(cancel);
            }

            return (
                <PlatformToastCard
                    actions={resolvedActions}
                    actionsAlwaysVisible={actionsAlwaysVisible}
                    description={resolvedDescription}
                    duration={duration}
                    icon={favoriteState === undefined ? icon : <FavoriteStatusIcon favorited={favoriteState} />}
                    lane={lane}
                    metadata={metadata}
                    onDismiss={() => sonnerToast.dismiss(toastId)}
                    status={status}
                    style={style}
                    timestamp={timestamp}
                    title={resolvedTitle}
                    toastId={toastId}
                    undo={undo}
                />
            );
        },
        {
            ...sonnerOptions,
            duration,
            toasterId,
            unstyled: true,
        },
    );
};

type PromiseResult<Data> = ReactNode | ((data: Data) => ReactNode);

export interface PlatformToastPromiseOptions<Data> extends Omit<PlatformToastOptions, 'description'> {
    description?: ReactNode | ((data: Data) => ReactNode);
    error?: PromiseResult<unknown>;
    finally?: () => void | Promise<void>;
    loading?: ReactNode;
    success?: PromiseResult<Data>;
}

const resolvePromiseResult = <Data,>(result: PromiseResult<Data> | undefined, data: Data, fallback: ReactNode) =>
    result === undefined ? fallback : typeof result === 'function' ? result(data) : result;

const promiseToast = <Data,>(
    promiseOrFactory: Promise<Data> | (() => Promise<Data>),
    options: PlatformToastPromiseOptions<Data> = {},
) => {
    const {
        description,
        error: errorResult,
        finally: finalize,
        loading = 'Action in Progress',
        success: successResult,
        ...toastOptions
    } = options;
    const promise = typeof promiseOrFactory === 'function' ? promiseOrFactory() : promiseOrFactory;
    const toastId = createToast('loading', loading, {
        ...toastOptions,
        description:
            typeof description === 'function'
                ? DEFAULT_DESCRIPTIONS.loading
                : (description ?? DEFAULT_DESCRIPTIONS.loading),
        duration: Infinity,
    });

    void promise
        .then((data) => {
            const successTitle = resolvePromiseResult(successResult, data, 'Action Completed');
            const resolvedDescription = typeof description === 'function' ? description(data) : description;

            createToast('success', successTitle, {
                ...toastOptions,
                description: resolvedDescription ?? DEFAULT_DESCRIPTIONS.success,
                id: toastId,
            });
        })
        .catch((error: unknown) => {
            const errorTitle = resolvePromiseResult(errorResult, error, 'Action Unsuccessful');

            createToast('error', errorTitle, {
                ...toastOptions,
                description: DEFAULT_DESCRIPTIONS.error,
                id: toastId,
            });
        })
        .finally(() => void finalize?.());

    return { unwrap: () => promise };
};

type CompatibleToast = ((message: ToastTitle, options?: PlatformToastOptions) => ToastId) & {
    custom: typeof sonnerToast.custom;
    dismiss: typeof sonnerToast.dismiss;
    error: (message: ToastTitle, options?: PlatformToastOptions) => ToastId;
    getHistory: typeof sonnerToast.getHistory;
    getToasts: typeof sonnerToast.getToasts;
    inApp: (message: ToastTitle, options?: PlatformToastOptions) => ToastId;
    info: (message: ToastTitle, options?: PlatformToastOptions) => ToastId;
    loading: (message: ToastTitle, options?: PlatformToastOptions) => ToastId;
    message: (message: ToastTitle, options?: PlatformToastOptions) => ToastId;
    promise: typeof promiseToast;
    success: (message: ToastTitle, options?: PlatformToastOptions) => ToastId;
    warning: (message: ToastTitle, options?: PlatformToastOptions) => ToastId;
};

export const toast = Object.assign(
    (message: ToastTitle, options?: PlatformToastOptions) => createToast('default', message, options),
    {
        custom: sonnerToast.custom,
        dismiss: sonnerToast.dismiss,
        error: (message: ToastTitle, options?: PlatformToastOptions) => createToast('error', message, options),
        getHistory: sonnerToast.getHistory,
        getToasts: sonnerToast.getToasts,
        inApp: (message: ToastTitle, options?: PlatformToastOptions) =>
            createToast('default', message, options, 'in-app'),
        info: (message: ToastTitle, options?: PlatformToastOptions) => createToast('info', message, options),
        loading: (message: ToastTitle, options?: PlatformToastOptions) => createToast('loading', message, options),
        message: (message: ToastTitle, options?: PlatformToastOptions) => createToast('default', message, options),
        promise: promiseToast,
        success: (message: ToastTitle, options?: PlatformToastOptions) => createToast('success', message, options),
        warning: (message: ToastTitle, options?: PlatformToastOptions) => createToast('warning', message, options),
    },
) satisfies CompatibleToast;

/** Strict, description-required API for new call sites; `toast` remains a normalized migration facade. */
export const platformToast = {
    error: (message: ToastTitle, options: StrictPlatformToastOptions) => createToast('error', message, options),
    feedback: (message: ToastTitle, options: StrictPlatformToastOptions) => createToast('default', message, options),
    inApp: (message: ToastTitle, options: StrictPlatformToastOptions) =>
        createToast('default', message, options, 'in-app'),
    info: (message: ToastTitle, options: StrictPlatformToastOptions) => createToast('info', message, options),
    loading: (message: ToastTitle, options: StrictPlatformToastOptions) => createToast('loading', message, options),
    success: (message: ToastTitle, options: StrictPlatformToastOptions) => createToast('success', message, options),
    warning: (message: ToastTitle, options: StrictPlatformToastOptions) => createToast('warning', message, options),
};
